import React from "react";
import { createPortal } from "react-dom";
import { IconClose } from "./Icons";

// Shared form/modal primitives for the coupon & promo admin pages. Mirrors the
// visual conventions used by the cancellation drawers (brand #0069d9, inputs
// on #f8f9fb, .4rem radius).

export const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.45rem 0.6rem",
  fontSize: ".88rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  boxSizing: "border-box",
};

export function ModalShell({
  title,
  onClose,
  children,
  footer,
  width = 520,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}) {
  // Portal a document.body: renderizado inline, el modal queda atrapado en el
  // stacking context de su ancestro (p.ej. el aside sticky de LeadDetail, cuya
  // cabecera lo tapaba). zIndex 150: sobre la UI, bajo los Dialog (200).
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 150,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "3rem 1rem",
        overflow: "auto",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: `min(${width}px, 96vw)`,
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 10px 32px rgba(0,0,0,.2)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 6rem)",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.9rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
              padding: "0.85rem 1.1rem",
              borderTop: "1px solid #e9ecef",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label style={{ display: "grid", gap: "0.3rem", marginBottom: "0.85rem" }}>
      <span
        style={{
          fontSize: ".72rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ fontSize: ".7rem", color: "#adb5bd" }}>{hint}</span>
      )}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  disabled,
  placeholder,
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      maxLength={maxLength}
      style={inputStyle}
    />
  );
}

export function NumberInput({
  value,
  onChange,
  disabled,
  min,
  max,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      min={min}
      max={max}
      style={inputStyle}
    />
  );
}

export function DateInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={inputStyle}
    />
  );
}

export function TextArea({
  value,
  onChange,
  disabled,
  rows = 3,
  placeholder,
  maxLength,
  onPaste,
  onKeyDown,
  textareaRef,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  // Opcionales: el composer de notas los usa para pegar imágenes inline
  // (interceptar el paste e insertar el token en la posición del cursor) y
  // para el atajo Ctrl/Cmd+Enter de publicar.
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}) {
  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onPaste={onPaste}
      onKeyDown={onKeyDown}
      disabled={disabled}
      rows={rows}
      placeholder={placeholder}
      maxLength={maxLength}
      style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
    />
  );
}

export function Select({
  value,
  onChange,
  disabled,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{ ...inputStyle, appearance: "auto" }}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#fde2e2",
        color: "#b32424",
        fontSize: ".8rem",
      }}
    >
      {children}
    </div>
  );
}

export function PrimaryButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={primaryBtn}>
      {children}
    </button>
  );
}

export function GhostButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={ghostBtn}>
      {children}
    </button>
  );
}

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
